import { ICellEditorAngularComp } from '@ag-grid-community/angular';
import { ICellEditorParams } from '@ag-grid-community/core';
import { AfterViewInit, Component, ElementRef, HostListener, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatCalendar, MatDatepicker } from '@angular/material/datepicker';
import { TranslateService } from '@app/core/services/translate.service';
import { UserStore } from '@app/core/services/user.store';
import type { GridRefreshableCellEditor } from '../../helpers/grid-active-editor.helpers';

type DateCellEditorParams = ICellEditorParams & { onCellEditorValueChanged?: () => void };

@Component({
    selector: 'tt-date-cell-editor',
    templateUrl: './date-cell-editor.component.html',
    styleUrls: ['./date-cell-editor.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class DateCellEditorComponent implements ICellEditorAngularComp, AfterViewInit, GridRefreshableCellEditor {
    /**
     * Refernce to the datetime html input element.
     */
    @ViewChild('inputRef')
    public inputRef?: ElementRef;

    /**
     * Cell editor parameters passed from the cell.
     */
    public params?: DateCellEditorParams;

    public dateFormat?: string;

    /**
     * The date value.
     */
    public value: Date | null = null;

    /**
     * Whether the value in the cell has been edited since editing started.
     */
    public hasBeenEdited: boolean = false;

    public placeholder: string = '';

    /**
     * Reference to the date picker component.
     */
    @ViewChild(MatDatepicker) picker?: MatDatepicker<Date>;

    @HostListener('keydown', ['$event'])
    public keydown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.params?.api.stopEditing(true);
            this.params?.api.setFocusedCell(this.params.rowIndex, this.params.colDef.field!);
        }
    }

    constructor(
        private userStore: UserStore,
        translate: TranslateService,
    ) {
        this.userStore.dateFormatChanged.subscribe(async (format) => {
            this.dateFormat = format.replaceAll('m', 'M');
            this.placeholder = await translate.translate(format);
        });
    }

    private markAsEdited(): void {
        this.hasBeenEdited = true;
        this.params?.onCellEditorValueChanged?.();
    }

    /**
     * Sets the date value when changed.
     *
     * @param event string representation of the new date.
     */
    onDateChanged(event: Date | string | null, markAsEdited: boolean = true) {
        if (markAsEdited === true) {
            this.markAsEdited();
        }

        this.value = typeof event === 'string' ? (event ? new Date(event) : null) : event;
    }

    public syncValueFromRow(value: unknown): void {
        this.onDateChanged(value as Date | string | null, false);
        setTimeout(() => {
            this.inputRef?.nativeElement.select();
        }, 1);
    }

    agInit(params: DateCellEditorParams): void {
        this.params = params;

        console.dir(this.params.data[this.params.column.getColId()]);
        this.value = !!this.params.data[this.params.column.getColId()] ? new Date(this.params.data[this.params.column.getColId()]) : null;
    }

    getValue() {
        return this.value;
    }

    ngAfterViewInit() {
        window.setTimeout(() => {
            if (!!this.params && this.inputRef && /^\d+$/.test(this.params.eventKey || '')) {
                this.inputRef.nativeElement.value = this.params.eventKey;
                this.inputRef?.nativeElement.focus();
            } else {
                this.inputRef?.nativeElement.focus();
                this.inputRef?.nativeElement.select();
            }
        });
    }

    onCalendarOpened() {
        setTimeout(() => {
            const calendar = (this.picker as any)._componentRef.instance._calendar;
            if (!calendar) return;

            this.appendWeekNumbers(calendar, true);

            // Trigger når man bytter måned/år med pilene
            calendar.stateChanges.subscribe(() => {
                setTimeout(() => this.appendWeekNumbers(calendar), 0);
            });

            // Trigger når man bytter mellom dato-visning og måned-valg-visning
            calendar.viewChanged.subscribe(() => {
                setTimeout(() => this.appendWeekNumbers(calendar), 0);
            });
        });
    }

    private getIsoWeek(date: Date): number {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    }

    private ensureWrapperExists(datepickerContent: HTMLElement) {
        let wrapper = document.getElementById('week-number-wrapper');

        if (!wrapper && datepickerContent) {
            wrapper = document.createElement('div');
            wrapper.id = 'week-number-wrapper';

            datepickerContent.parentNode?.insertBefore(wrapper, datepickerContent);

            const weekContent = document.createElement('div');
            weekContent.id = 'week-number-content';

            const table = document.createElement('table');
            table.id = 'week-number-table';

            weekContent.appendChild(table);
            wrapper.appendChild(weekContent);
            wrapper.appendChild(datepickerContent);
        }
        return wrapper;
    }

    private async appendWeekNumbers(calendar: MatCalendar<any>, firstOpened: boolean = false) {
        const dateAdapter = (calendar as any)._dateAdapter;
        const datepickerContent = document.querySelector('.mat-datepicker-content') as HTMLElement;
        if (!datepickerContent) return;

        this.ensureWrapperExists(datepickerContent);

        const table = document.getElementById('week-number-table');
        const weekContent = document.getElementById('week-number-content');
        if (!table || !weekContent) return;

        // 1. Sjekk visning. Hvis ikke 'month', deaktiver kolonnen
        if (calendar.currentView !== 'month') {
            weekContent.classList.add('is-hidden');
            table.innerHTML = '';
            return;
        }

        // 2. Aktiver kolonnen for månedsvisning
        weekContent.classList.remove('is-hidden');

        const calendarRows = document.querySelectorAll('.mat-calendar-body tr');
        let rowsHtml = '';

        calendarRows.forEach((row) => {
            const cells = row.querySelectorAll('.mat-calendar-body-cell');
            let weekNum: number | string = '';
            const rowHeight = (row as HTMLElement).offsetHeight || 40;

            for (let i = 0; i < cells.length; i++) {
                const cellText = (cells[i] as HTMLElement).innerText.trim();
                const dayValue = parseInt(cellText, 10);

                if (!isNaN(dayValue)) {
                    const dateInRow = dateAdapter.createDate(dateAdapter.getYear(calendar.activeDate), dateAdapter.getMonth(calendar.activeDate), dayValue);
                    weekNum = this.getIsoWeek(dateInRow);
                    break;
                }
            }
            rowsHtml += `<tr><td style="height: ${rowHeight}px;">${weekNum}</td></tr>`;
        });

        if (firstOpened) {
            await new Promise<void>((resolve) => setTimeout(() => resolve(), 150));
        }

        // 3. Juster posisjon basert på header-høyde
        const calendarBody = document.querySelector('.mat-calendar-body');
        if (calendarBody) {
            const bodyTop = calendarBody.getBoundingClientRect().top;
            const contentTop = datepickerContent.getBoundingClientRect().top;
            weekContent.style.paddingTop = `${bodyTop - contentTop}px`;
        }

        table.innerHTML = rowsHtml;
    }
}
